import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import CustomButton from '../../../../components/CustomButton/CustomButton';
import { CustomDropdown } from '@components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@stores/store';
import useCustomDashboardFilters from '@hooks/api/useCustomDashboardFilters';
import { useLazyGetUserModuleGamesQuery } from '@api/userModules';
import { setDashboardFilter } from '@stores/DashboardFilters/slice';

interface Game {
  id: string;
  name: string;
}

interface Filter {
  label: string;
  key: string;
  values: { id: string; label: string }[];
}

interface DownloadReportsHeaderProps {
  isDownloadModalOpen: boolean;
  setIsDownloadModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  submoduleId: string;
  selectedGame: Game | undefined;
  setSelectedGame: React.Dispatch<Game | undefined>;
  setDashboardLoader: React.Dispatch<React.SetStateAction<boolean>>;
}

const DownloadReportsHeader: FC<DownloadReportsHeaderProps> = ({
  isDownloadModalOpen,
  setIsDownloadModalOpen,
  submoduleId,
  selectedGame,
  setSelectedGame,
  setDashboardLoader,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const dashboardFilters = useSelector((state: RootState) => state?.DashboardFiltersData);
  const modules = useSelector((state: RootState) => state?.ModulesData);

  const { getDashboardFilterValues } = useCustomDashboardFilters();
  const [getUserModuleGames] = useLazyGetUserModuleGamesQuery();
  const [filters, setFilters] = useState<Filter[]>([]);

  const downloadReportsSelections = dashboardFilters.dashboards['DownloadReports' as any] as Game;

  useEffect(() => {
    if (submoduleId) {
      getDashboardFilterValues(submoduleId).then(async (response) => {
        if (response) {
          const gamesParam = response?.game_id?.values || [];
          const gamesDetails = await getUserModuleGames({ submoduleId, gamesParam }).unwrap();

          const filterData = Object.values(response).map((res: any) => ({
            label: res.displayName,
            key: res.name,
            values: res.values
              .map((v: string) => {
                const game = gamesDetails?.data.find((d: any) => d.gameId === v);
                return {
                  id: v,
                  label: game?.dealName ? `${game?.dealName} - ${v}` : v,
                };
              })
              .sort((a: any, b: any) => a.label.localeCompare(b.label)),
          }));

          setFilters(filterData);
          setDashboardLoader(false);

          if (downloadReportsSelections) {
            setSelectedGame(downloadReportsSelections);
          } else {
            const defaultFilterId = modules?.modules
              ?.flatMap((m: any) => m.submodules)
              ?.find((s: any) => s.id === submoduleId)?.defaultFilter;

            const gameFilterOptions = filterData.find((f) => f.key === 'gameId')?.values;
            const defaultGame =
              gameFilterOptions?.find((g: any) => g.id === defaultFilterId) ||
              gameFilterOptions?.[0];
            setSelectedGame({
              id: defaultGame?.id,
              name: defaultGame?.label,
            });
          }
        }
      });
    }
  }, [submoduleId]);

  const handleFilterChange = (value: any) => {
    const filteredGame = filters
      .find((f: any) => f.key === 'gameId')
      ?.values?.find((game: any) => game.id === value);
    if (filteredGame) {
      dispatch(setDashboardFilter({ DownloadReports: { id: filteredGame.id } }));
      setSelectedGame({
        id: filteredGame?.id,
        name: filteredGame?.label,
      });
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? '' : 'center',
        gap: '0.25rem',
        mt: '0.5rem',
        mb: '1.5rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '0.5rem',
          alignItems: isMobile ? '' : 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: isMobile ? '1.5rem' : '2rem',
            fontWeight: 600,
            color: '#1B1C17',
            lineHeight: '1.5rem',
          }}
        >
          Download Reports : {filters[0]?.values?.length === 1 ? selectedGame?.name : ''}
        </Typography>
        {filters.map((filter) =>
          filter.values.length > 1 ? (
            <CustomDropdown
              key={filter.key}
              selectedOption={selectedGame?.id || ''}
              dropdownOptions={filter.values}
              handleChange={(event) => handleFilterChange(event.target.value)}
              wrapperSx={{
                fontSize: isMobile ? '1rem' : '1.5rem',
                color: '#1B1C17',
                lineHeight: 'none',
                border: '1px solid #2F736E46',
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: '15rem',
                  },
                },
              }}
            />
          ) : null,
        )}
      </Box>
      {isDownloadModalOpen ? (
        <CustomButton
          onClick={() => {}}
          name="Downloading..."
          color="#6C6C6C"
          borderColor="#2E4A4F"
          width={isMobile ? '100%' : '10.2rem'}
          disabled
        />
      ) : (
        <CustomButton
          onClick={() => setIsDownloadModalOpen(true)}
          name="Download Report"
          color="#6C6C6C"
          borderColor="#2E4A4F"
          width={isMobile ? '100%' : '10.2rem'}
        />
      )}
    </Box>
  );
};

export default DownloadReportsHeader;
